import { useState, useEffect } from 'react';

import { useParams } from 'react-router-dom';
import { TldrawUiDialogCloseButton, TldrawUiDialogBody, TldrawUiDialogTitle } from 'tldraw';

import { useGetDocIds } from '@/api/queries/useGetDocIds';
import { Button } from '@/new/components/shadcn/button';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Textarea } from '@/new/components/shadcn/textarea';
import { MyOptionType } from '@/types';

interface NodeEditingModalProps {
  onClose: () => void;
  onSave: (nodeData: { dateText: string; description: string; documentNames: string }) => void;
  initialData: {
    dateText: string;
    description: string;
    documentNames: string;
  };
}

export const NodeEditingModal = ({ onClose, onSave, initialData }: NodeEditingModalProps) => {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return null;

  const [date, setDate] = useState(initialData.dateText);
  const [description, setDescription] = useState(initialData.description);
  const [selectedDoc, setSelectedDoc] = useState<MyOptionType>(null);

  const { data: allDocuments } = useGetDocIds(matterId, subsetId);

  // Initialize selected document based on initial document names
  useEffect(() => {
    if (initialData.documentNames && allDocuments) {
      const documentName = initialData.documentNames.split(', ')[0]; // Take first document only
      const initialSelectedDoc = allDocuments?.documents?.filter(Boolean).find((doc) => doc.file.name === documentName);

      if (initialSelectedDoc) {
        setSelectedDoc({
          label: initialSelectedDoc.file.name,
          value: initialSelectedDoc.id,
        });
      }
    }
  }, [initialData.documentNames, allDocuments]);

  const handleSave = () => {
    if (description.trim()) {
      const documentNames = selectedDoc?.label || '';
      onSave({
        dateText: date.trim(),
        description: description.trim(),
        documentNames: documentNames,
      });
    }
  };

  const handleClose = () => {
    onClose();
  };

  const documentOptions =
    allDocuments?.documents?.filter(Boolean).map((doc) => ({
      label: doc.file.name,
      value: doc.id,
    })) || [];

  return (
    <>
      <div className="border-b border-gray-200 px-7 py-2">
        <div className="flex items-center justify-between">
          <TldrawUiDialogTitle>
            <p className="text-base font-semibold text-gray-900">Edit Chronology Event</p>
          </TldrawUiDialogTitle>
          <TldrawUiDialogCloseButton />
        </div>
      </div>
      <TldrawUiDialogBody className="w-[450px]">
        <div className="px-4 py-1">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="date">Date</Label>
              <Input
                type="text"
                id="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                placeholder="e.g., January 15, 2023"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="document">Document</Label>
              <select
                value={selectedDoc?.value}
                onChange={(e) => setSelectedDoc({ label: e.target.value, value: e.target.value })}
                className="flex h-9 w-full items-center justify-between rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap duration-150 focus:border-gray-600 focus:ring-gray-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-gray-500 [&>span]:line-clamp-1 [&>span]:break-all [&>span]:text-ellipsis"
              >
                {documentOptions.map((doc) => (
                  <option key={doc.value} value={doc.value}>
                    {doc.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter event description..."
                rows={3}
                className="resize-none"
              />
            </div>
          </div>

          <div className="mt-4 flex justify-between gap-3">
            <Button onClick={handleClose}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} disabled={!description.trim()}>
              Save Changes
            </Button>
          </div>
        </div>
      </TldrawUiDialogBody>
    </>
  );
};
